import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import {
  Database, BarChart3, Globe, Palette, ArrowRight, Check,
  Mail, Sparkles, Zap, Shield, Clock,
  Menu, X, Star, ChevronRight,
  TrendingUp, Target, Rocket, Phone, Code2, Cpu, Activity, Play
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import DataFlowCanvas from "@/components/DataFlowCanvas";
import TerminalWindow from "@/components/TerminalWindow";
import TypewriterText from "@/components/TypewriterText";
import AnimatedStat from "@/components/AnimatedStat";

const LOGO_URL = "/brand/tdw-logo.2b8d7f.webp";

// Stagger reveal hook
function useStaggerReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
        }
      }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal, .stagger-reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

export default function Home() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useStaggerReveal();

  useEffect(() => {
    document.title = 'Data Management & Dashboards | TechDataWeb';
  }, []);

  useEffect(() => {
    let frame = 0;
    const handleScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        setScrolled(window.scrollY > 50);
        frame = 0;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const services = [
    {
      icon: <Database size={28} />,
      title: "Data Management",
      description: "Transform scattered spreadsheets into one clear, organized system you can trust.",
      link: "/services/data-management",
      tag: "Most Popular",
      tagColor: '#22c55e',
      tagBg: 'rgba(34,197,94,0.12)',
      tagBorder: 'rgba(34,197,94,0.25)',
      accent: '#4f8cff',
      accentBg: 'rgba(79,140,255,0.10)',
      gradient: 'linear-gradient(135deg, rgba(79,140,255,0.15) 0%, rgba(79,140,255,0.03) 100%)',
      glowColor: 'rgba(79,140,255,0.25)',
      featured: true,
      bullets: ['Combine all spreadsheets', 'Clean & standardize data', 'Build tracking systems'],
    },
    {
      icon: <BarChart3 size={28} />,
      title: "Custom Dashboards",
      description: "Excel & Google Sheets dashboards with live charts, filters, and auto-calculations.",
      link: "/services/custom-dashboards",
      tag: "",
      accent: '#a78bfa',
      accentBg: 'rgba(167,139,250,0.10)',
      gradient: 'linear-gradient(135deg, rgba(167,139,250,0.15) 0%, rgba(167,139,250,0.03) 100%)',
      glowColor: 'rgba(167,139,250,0.25)',
      featured: false,
      bullets: ['KPI summary tabs', 'Pivot tables & charts', 'Auto-calculated reports'],
    },
    {
      icon: <Globe size={28} />,
      title: "Website Design",
      description: "Fast, modern websites that attract clients and convert visitors — built in days.",
      link: "/services/website-design",
      tag: "",
      accent: '#34d399',
      accentBg: 'rgba(52,211,153,0.10)',
      gradient: 'linear-gradient(135deg, rgba(52,211,153,0.15) 0%, rgba(52,211,153,0.03) 100%)',
      glowColor: 'rgba(52,211,153,0.25)',
      featured: false,
      bullets: ['Mobile-first design', 'SEO-ready structure', 'Custom domain setup'],
    },
    {
      icon: <Palette size={28} />,
      title: "Brand Identity",
      description: "Complete branding: logos, business cards, and full brand kits that make you memorable.",
      link: "/services/brand-identity",
      tag: "",
      accent: '#f472b6',
      accentBg: 'rgba(244,114,182,0.10)',
      gradient: 'linear-gradient(135deg, rgba(244,114,182,0.15) 0%, rgba(244,114,182,0.03) 100%)',
      glowColor: 'rgba(244,114,182,0.25)',
      featured: false,
      bullets: ['Logo + color palette', 'Business card design', 'Social media assets'],
    },
    {
      icon: <Sparkles size={28} />,
      title: "AI & Automation",
      description: "Automate repetitive workflows with AI tools — save hours every single week.",
      link: "/services/ai-automation",
      tag: "🔥 Hot",
      tagColor: '#fb923c',
      tagBg: 'rgba(251,146,60,0.12)',
      tagBorder: 'rgba(251,146,60,0.25)',
      accent: '#fb923c',
      accentBg: 'rgba(251,146,60,0.10)',
      gradient: 'linear-gradient(135deg, rgba(251,146,60,0.15) 0%, rgba(251,146,60,0.03) 100%)',
      glowColor: 'rgba(251,146,60,0.30)',
      featured: false,
      bullets: ['Zapier / Make workflows', 'AI content & reports', 'Custom integrations'],
    },
    {
      icon: <TrendingUp size={28} />,
      title: "Data Analytics",
      description: "Turn raw data into actionable insights with advanced analytics and visual reports.",
      link: "/services/data-analytics",
      tag: "",
      accent: '#38bdf8',
      accentBg: 'rgba(56,189,248,0.10)',
      gradient: 'linear-gradient(135deg, rgba(56,189,248,0.15) 0%, rgba(56,189,248,0.03) 100%)',
      glowColor: 'rgba(56,189,248,0.25)',
      featured: false,
      bullets: ['Trend analysis', 'Visual reports', 'Predictive insights'],
    },
    {
      icon: <Target size={28} />,
      title: "Logo Design",
      description: "Professional logos with multiple concepts, unlimited revisions, and brand guidelines.",
      link: "/services/logo-design",
      tag: "",
      accent: '#e879f9',
      accentBg: 'rgba(232,121,249,0.10)',
      gradient: 'linear-gradient(135deg, rgba(232,121,249,0.15) 0%, rgba(232,121,249,0.03) 100%)',
      glowColor: 'rgba(232,121,249,0.25)',
      featured: false,
      bullets: ['3+ logo concepts', 'Unlimited revisions', 'All file formats'],
    },
    {
      icon: <Star size={28} />,
      title: "Excel Training",
      description: "1-on-1 Excel coaching: formulas, pivot tables, and advanced features tailored to your job.",
      link: "/services/excel-training",
      tag: "",
      accent: '#facc15',
      accentBg: 'rgba(250,204,21,0.10)',
      gradient: 'linear-gradient(135deg, rgba(250,204,21,0.15) 0%, rgba(250,204,21,0.03) 100%)',
      glowColor: 'rgba(250,204,21,0.25)',
      featured: false,
      bullets: ['Custom curriculum', 'Live 1-on-1 sessions', 'Practice exercises'],
    },
  ];

  const stats = [
    { number: "100", suffix: "+", label: "Projects Delivered" },
    { number: "50", suffix: "+", label: "Happy Clients" },
    { number: "5", suffix: "+", label: "Years Experience" },
    { number: "24", suffix: "h", label: "Response Time" },
  ];

  const faqs = [
    { q: "I don't know where to start. Can you still help?", a: "Absolutely. That's what the free consultation is for. We'll look at your current situation together and I'll recommend the best path forward — no pressure, no jargon." },
    { q: "Do you only work with Excel?", a: "Not at all. I work with Excel, Google Sheets, and various other tools. We'll use whatever makes the most sense for your specific needs." },
    { q: "What is included in the Business Launch package?", a: "Everything you need to launch professionally: a custom logo, print-ready business cards, a beautiful website, domain setup, and guidance through the whole process." },
    { q: "Do you provide training after the project?", a: "Yes. Every project includes a training session so you understand how to use and maintain your new tools, plus documentation you can reference anytime." },
  ];

  const [formData, setFormData] = useState({ name: "", email: "", org: "", message: "", contact: "Email", budget: "" });

  const contactMutation = trpc.contact.submit.useMutation({
    onSuccess: () => {
      toast.success("Message sent! I'll get back to you within 24 hours.");
      setFormData({ name: "", email: "", org: "", message: "", contact: "Email", budget: "" });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to send message. Please email us directly.");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    contactMutation.mutate(formData);
  };

  // Color tokens
  const C = {
    base: '#111827',
    surface: '#1a2235',
    raised: '#1f2d44',
    border: 'rgba(255,255,255,0.07)',
    borderMid: 'rgba(255,255,255,0.12)',
    borderBlue: 'rgba(79,140,255,0.22)',
    blue: '#4f8cff',
    blueDark: '#2d6ce0',
    blueSubtle: 'rgba(79,140,255,0.08)',
    text: '#e8edf5',
    textSec: '#8fa3bf',
    textMuted: '#5a7090',
  };

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: C.base, color: C.text }}>

      {/* ===== NAVIGATION ===== */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          backgroundColor: scrolled ? 'rgba(17,24,39,0.97)' : 'rgba(17,24,39,0.80)',
          backdropFilter: 'blur(16px)',
          borderBottom: `1px solid ${scrolled ? C.borderMid : C.border}`,
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2.5 cursor-pointer">
              <img src={LOGO_URL} alt="TDW" width={90} height={40} className="h-10 w-auto logo-glow" />
              <div className="flex flex-col leading-none">
                <span style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: '0.95rem', color: C.text, letterSpacing: '-0.01em' }}>TechDataWeb</span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: '0.65rem', color: C.textMuted, letterSpacing: '0.04em' }}>techdataweb.com</span>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-7">
              <button onClick={() => scrollToSection('services')} className="nav-link">Services</button>
              <button onClick={() => scrollToSection('about')} className="nav-link">About</button>
              <button onClick={() => scrollToSection('faq')} className="nav-link">FAQ</button>
              <button onClick={() => scrollToSection('contact')} className="nav-link">Contact</button>
              <button onClick={() => scrollToSection('contact')} className="btn-primary btn-primary-glow text-sm py-2 px-4">
                Book Free Call <ArrowRight size={14} />
              </button>
            </div>

            <button
              className="md:hidden p-2 rounded-lg"
              style={{ color: C.text }}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-navigation"
            >
              {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div id="mobile-navigation" className="md:hidden border-t px-4 py-5 space-y-3" style={{ backgroundColor: C.surface, borderColor: C.border }}>
            <button onClick={() => scrollToSection('services')} className="block w-full text-left py-2 nav-link">Services</button>
            <button onClick={() => scrollToSection('about')} className="block w-full text-left py-2 nav-link">About</button>
            <button onClick={() => scrollToSection('faq')} className="block w-full text-left py-2 nav-link">FAQ</button>
            <button onClick={() => scrollToSection('contact')} className="block w-full text-left py-2 nav-link">Contact</button>
            <button onClick={() => scrollToSection('contact')} className="w-full btn-primary justify-center mt-2">Book Free Call</button>
          </div>
        )}
      </nav>

      {/* ===== HERO ===== */}
      <section className="pt-16 relative overflow-hidden tech-grid-bg" style={{ backgroundColor: C.base, minHeight: '100vh' }}>
        {/* Animated particle canvas background */}
        <DataFlowCanvas />

        {/* Radial glow overlay */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse 70% 50% at 20% 50%, rgba(79,140,255,0.07) 0%, transparent 70%)',
        }} />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-64px)] py-20">

            {/* Left — headline + CTAs */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6 text-xs font-medium" style={{ background: C.blueSubtle, color: C.blue, border: `1px solid ${C.borderBlue}` }}>
                <Zap size={12} /> Data · Dashboards · Websites · Branding
              </div>

              <h1
                className="font-display mb-3 min-h-[3.3em] lg:min-h-[4.4em]"
                style={{ fontSize: 'clamp(2.4rem, 5vw, 3.8rem)', lineHeight: 1.1, color: C.text }}
              >
                Your Business Deserves<br />
                <TypewriterText />
              </h1>

              <p className="mb-8 text-lg leading-relaxed max-w-lg" style={{ color: C.textSec, fontFamily: "'Inter', sans-serif" }}>
                I help small businesses organize their data, build smart dashboards, and launch complete brands — without the big-agency price tag.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-10">
                <button onClick={() => scrollToSection('contact')} className="btn-primary btn-primary-glow text-base">
                  <Rocket size={16} /> Book a Free Consultation
                </button>
                <button onClick={() => scrollToSection('services')} className="btn-outline text-base">
                  Explore Services <ChevronRight size={16} />
                </button>
              </div>

              {/* Trust signals */}
              <div className="flex flex-wrap gap-5">
                {[
                  { icon: <Check size={14} />, label: "Free 20-min consultation" },
                  { icon: <Check size={14} />, label: "No contracts required" },
                  { icon: <Check size={14} />, label: "Results in days, not months" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-sm" style={{ color: C.textSec }}>
                    <span style={{ color: C.blue }}>{item.icon}</span>
                    {item.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Right — terminal + problem/solution */}
            <div className="reveal flex flex-col gap-4">
              {/* Terminal window */}
              <div className="relative">
                <div className="scan-line"></div>
                <TerminalWindow />
              </div>

              {/* Problem / Solution mini cards */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 rounded-xl glow-border float-a" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                  <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-3 text-xs font-semibold" style={{ background: 'rgba(255,0,85,0.1)', color: '#ff5588', border: '1px solid rgba(255,0,85,0.2)' }}>
                    <X size={10} /> What You Have
                  </div>
                  <ul className="space-y-1.5">
                    {['Scattered spreadsheets', 'Manual repetitive work', 'No clear data picture'].map((p, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs" style={{ color: C.textSec }}>
                        <X size={11} className="mt-0.5 shrink-0" style={{ color: '#ff5588' }} />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-4 rounded-xl glow-border float-b" style={{ background: C.surface, border: `1px solid ${C.borderBlue}` }}>
                  <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-3 text-xs font-semibold" style={{ background: C.blueSubtle, color: C.blue, border: `1px solid ${C.borderBlue}` }}>
                    <Check size={10} /> What You Need
                  </div>
                  <ul className="space-y-1.5">
                    {['One organized system', 'Automated workflows', 'Clear insights & reports'].map((s, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs" style={{ color: C.textSec }}>
                        <Check size={11} className="mt-0.5 shrink-0" style={{ color: C.blue }} />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== STATS BAR ===== */}
      <section style={{ backgroundColor: C.surface, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 stagger-reveal">
            {stats.map((stat, i) => (
              <div key={i} className="p-5 rounded-xl text-center glow-border animated-border" style={{ background: C.raised, border: `1px solid ${C.border}` }}>
                <AnimatedStat value={`${stat.number}${stat.suffix}`} label={stat.label} delay={i * 120} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section id="services" className="py-24 tech-grid-bg" style={{ backgroundColor: C.base }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="mb-14 reveal">
            <div className="badge badge-primary mb-3">What I Do</div>
            <h2 className="font-display mb-3" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', color: C.text }}>
              Services That <span style={{ color: C.blue }}>Actually Work</span>
            </h2>
            <div className="section-divider"></div>
            <p className="text-base max-w-xl" style={{ color: C.textSec, fontFamily: "'Inter', sans-serif" }}>
              No fluff. Practical solutions that make your business run smoother and grow faster.
            </p>
          </div>

          {/* Featured card — Data Management spans full width on mobile, 2 cols on lg */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5 stagger-reveal">
            {/* Featured wide card */}
            <Link href={services[0].link} className="lg:col-span-1">
              <div
                className="relative overflow-hidden rounded-2xl cursor-pointer h-full group"
                style={{
                  background: services[0].gradient,
                  border: `1.5px solid ${services[0].glowColor}`,
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = `0 0 40px ${services[0].glowColor}, 0 8px 32px rgba(0,0,0,0.3)`; (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = 'none'; (e.currentTarget as HTMLElement).style.transform = 'none'; }}
              >
                {/* Top accent bar */}
                <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: `linear-gradient(90deg, ${services[0].accent}, transparent)` }} />
                <div className="p-6">
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: services[0].accentBg, color: services[0].accent }}>
                      {services[0].icon}
                    </div>
                    <span className="px-2.5 py-1 rounded-full text-xs font-bold" style={{ background: services[0].tagBg, color: services[0].tagColor, border: `1px solid ${services[0].tagBorder}` }}>
                      {services[0].tag}
                    </span>
                  </div>
                  <h3 className="font-display text-xl mb-2" style={{ color: C.text, fontFamily: "'Sora', sans-serif", fontWeight: 800 }}>{services[0].title}</h3>
                  <p className="text-sm leading-relaxed mb-5" style={{ color: C.textSec }}>{services[0].description}</p>
                  <ul className="space-y-2 mb-5">
                    {services[0].bullets?.map((b, bi) => (
                      <li key={bi} className="flex items-center gap-2 text-xs" style={{ color: C.textSec }}>
                        <span className="w-4 h-4 rounded-full flex items-center justify-center shrink-0" style={{ background: services[0].accentBg, color: services[0].accent }}><Check size={10} /></span>
                        {b}
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: services[0].accent }}>
                    Learn more <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </Link>

            {/* 2 stacked cards */}
            <div className="flex flex-col gap-5">
              {[services[1], services[2]].map((svc, i) => (
                <Link key={i} href={svc.link}>
                  <div
                    className="relative overflow-hidden rounded-2xl cursor-pointer group flex-1"
                    style={{ background: svc.gradient, border: `1.5px solid ${svc.glowColor}`, transition: 'all 0.3s ease', padding: '1.25rem 1.5rem' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = `0 0 30px ${svc.glowColor}, 0 6px 24px rgba(0,0,0,0.25)`; (e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = 'none'; (e.currentTarget as HTMLElement).style.transform = 'none'; }}
                  >
                    <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: `linear-gradient(90deg, ${svc.accent}, transparent)` }} />
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: svc.accentBg, color: svc.accent }}>{svc.icon}</div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-display text-base mb-1" style={{ color: C.text, fontFamily: "'Sora', sans-serif", fontWeight: 700 }}>{svc.title}</h3>
                        <p className="text-xs leading-relaxed" style={{ color: C.textSec }}>{svc.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-xs font-semibold mt-3" style={{ color: svc.accent }}>
                      Learn more <ArrowRight size={11} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* AI & Automation — featured hot card */}
            <Link href={services[4].link}>
              <div
                className="relative overflow-hidden rounded-2xl cursor-pointer h-full group"
                style={{ background: services[4].gradient, border: `1.5px solid ${services[4].glowColor}`, transition: 'all 0.3s ease' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = `0 0 40px ${services[4].glowColor}, 0 8px 32px rgba(0,0,0,0.3)`; (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = 'none'; (e.currentTarget as HTMLElement).style.transform = 'none'; }}
              >
                <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: `linear-gradient(90deg, ${services[4].accent}, transparent)` }} />
                <div className="p-6">
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: services[4].accentBg, color: services[4].accent }}>{services[4].icon}</div>
                    <span className="px-2.5 py-1 rounded-full text-xs font-bold" style={{ background: services[4].tagBg, color: services[4].tagColor, border: `1px solid ${services[4].tagBorder}` }}>{services[4].tag}</span>
                  </div>
                  <h3 className="font-display text-xl mb-2" style={{ color: C.text, fontFamily: "'Sora', sans-serif", fontWeight: 800 }}>{services[4].title}</h3>
                  <p className="text-sm leading-relaxed mb-5" style={{ color: C.textSec }}>{services[4].description}</p>
                  <ul className="space-y-2 mb-5">
                    {services[4].bullets?.map((b, bi) => (
                      <li key={bi} className="flex items-center gap-2 text-xs" style={{ color: C.textSec }}>
                        <span className="w-4 h-4 rounded-full flex items-center justify-center shrink-0" style={{ background: services[4].accentBg, color: services[4].accent }}><Check size={10} /></span>
                        {b}
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: services[4].accent }}>
                    Learn more <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {/* Bottom row — 5 remaining services */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 stagger-reveal">
            {[services[3], services[5], services[6], services[7]].map((svc, i) => (
              <Link key={i} href={svc.link} className="lg:col-span-1">
                <div
                  className="relative overflow-hidden rounded-2xl cursor-pointer group h-full"
                  style={{ background: svc.gradient, border: `1.5px solid ${svc.glowColor}`, transition: 'all 0.3s ease', padding: '1.25rem' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = `0 0 28px ${svc.glowColor}, 0 6px 20px rgba(0,0,0,0.25)`; (e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = 'none'; (e.currentTarget as HTMLElement).style.transform = 'none'; }}
                >
                  <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: `linear-gradient(90deg, ${svc.accent}, transparent)` }} />
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3" style={{ background: svc.accentBg, color: svc.accent }}>{svc.icon}</div>
                  <h3 className="font-display text-sm mb-1.5" style={{ color: C.text, fontFamily: "'Sora', sans-serif", fontWeight: 700 }}>{svc.title}</h3>
                  <p className="text-xs leading-relaxed mb-3" style={{ color: C.textSec }}>{svc.description}</p>
                  <div className="flex items-center gap-1 text-xs font-semibold" style={{ color: svc.accent }}>
                    Learn more <ArrowRight size={11} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
            {/* CTA card */}
            <div
              className="relative overflow-hidden rounded-2xl cursor-pointer group lg:col-span-1"
              style={{ background: 'linear-gradient(135deg, rgba(79,140,255,0.18) 0%, rgba(79,140,255,0.06) 100%)', border: `1.5px dashed rgba(79,140,255,0.35)`, transition: 'all 0.3s ease', padding: '1.25rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', minHeight: '160px' }}
              onClick={() => scrollToSection('contact')}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = `0 0 28px rgba(79,140,255,0.25)`; (e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = 'none'; (e.currentTarget as HTMLElement).style.transform = 'none'; }}
            >
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3" style={{ background: 'rgba(79,140,255,0.12)', color: '#4f8cff' }}><Rocket size={22} /></div>
              <p className="text-xs font-semibold mb-1" style={{ color: '#e8edf5' }}>Not sure which?</p>
              <p className="text-xs mb-3" style={{ color: '#8fa3bf' }}>Book a free call — we'll figure it out together.</p>
              <div className="flex items-center gap-1 text-xs font-bold" style={{ color: '#4f8cff' }}>Book Free Call <ArrowRight size={11} /></div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TECH CREDIBILITY STRIP ===== */}
      <section style={{ backgroundColor: C.surface, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-wrap items-center justify-center gap-8 reveal">
            {[
              { icon: <Database size={16} />, label: "MySQL · PostgreSQL · Excel" },
              { icon: <Code2 size={16} />, label: "Python · JavaScript · React" },
              { icon: <BarChart3 size={16} />, label: "Power BI · Tableau · Sheets" },
              { icon: <Cpu size={16} />, label: "OpenAI · Zapier · Make.com" },
              { icon: <Activity size={16} />, label: "REST APIs · Webhooks · n8n" },
            ].map((tech, i) => (
              <div key={i} className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: C.raised, border: `1px solid ${C.border}`, color: C.textSec, fontFamily: "'Inter', sans-serif", fontSize: '0.8rem' }}>
                <span style={{ color: C.blue }}>{tech.icon}</span>
                {tech.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ABOUT ===== */}
      <section id="about" className="py-24" style={{ backgroundColor: C.base }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            <div className="reveal">
              <div className="badge badge-primary mb-4">About</div>
              <h2 className="font-display mb-4" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', color: C.text }}>
                Why Work With <span style={{ color: C.blue }}>TechDataWeb?</span>
              </h2>
              <div className="section-divider"></div>
              <p className="text-base leading-relaxed mb-6" style={{ color: C.textSec, fontFamily: "'Inter', sans-serif" }}>
                I work as a data manager and see the same problem everywhere: too many spreadsheets, too many tools, and no clear picture. I help you organize everything into clean systems, easy dashboards, and simple websites that support your real work — not create more of it.
              </p>
              <p className="text-base leading-relaxed mb-8" style={{ color: C.textSec, fontFamily: "'Inter', sans-serif" }}>
                Every solution is tailored to your specific situation. No templates, no one-size-fits-all. Just practical work that delivers results.
              </p>
              <button onClick={() => scrollToSection('contact')} className="btn-primary btn-primary-glow">
                Start a Conversation <ArrowRight size={16} />
              </button>
            </div>

            <div className="space-y-4">
              <figure className="about-video-shell reveal">
                <div className="relative aspect-video overflow-hidden">
                  <video
                    className="about-video"
                    controls
                    playsInline
                    preload="metadata"
                    poster="/manus-storage/techdataweb-introduction-poster.20261013_1d016a15.webp"
                    aria-describedby="about-video-description"
                  >
                    <source src="/manus-storage/techdataweb-introduction.20261013_18c613fe.mp4" type="video/mp4" />
                    Your browser does not support embedded video. Please contact TechDataWeb to view the introduction.
                  </video>
                  <div className="about-video-label" aria-hidden="true">
                    <Play size={12} fill="currentColor" />
                    Watch our 30-second overview
                  </div>
                </div>
                <figcaption className="about-video-caption">
                  <span>From data chaos to clear business systems.</span>
                  <span className="about-video-caption-meta">Data · Dashboards · Websites · Branding</span>
                </figcaption>
                <p id="about-video-description" className="sr-only">
                  A 30-second TechDataWeb overview showing a cluttered spreadsheet workspace transform into organized data, dashboards, websites, branding, and automated workflows. The video contains instrumental music and on-screen text, with no spoken dialogue.
                </p>
              </figure>

              <div className="grid grid-cols-2 gap-4 stagger-reveal">
                {[
                  { icon: <Sparkles size={20} />, title: "Plain Language", desc: "No tech jargon — just clear explanations you can act on." },
                  { icon: <Shield size={20} />, title: "Realistic Solutions", desc: "Only what you actually need, nothing unnecessary." },
                  { icon: <Zap size={20} />, title: "Fast Delivery", desc: "Results in days, not months. One-time or ongoing." },
                  { icon: <Clock size={20} />, title: "Always Honest", desc: "Transparent pricing, clear timelines, no surprises." },
                ].map((v, i) => (
                  <div key={i} className={`p-5 rounded-xl glow-border animated-border ${i % 2 === 0 ? 'float-a' : 'float-b'}`}
                    style={{ background: C.surface, border: `1px solid ${C.border}`, animationDelay: `${i * 0.5}s` }}>
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ background: C.blueSubtle, color: C.blue }}>
                      {v.icon}
                    </div>
                    <h3 className="font-semibold text-sm mb-1.5" style={{ color: C.text, fontFamily: "'Sora', sans-serif" }}>{v.title}</h3>
                    <p className="text-xs leading-relaxed" style={{ color: C.textSec, fontFamily: "'Inter', sans-serif" }}>{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA BANNER ===== */}
      <section className="py-16 relative overflow-hidden" style={{ background: `linear-gradient(135deg, #1a2a4a 0%, #1f3060 100%)`, borderTop: `1px solid ${C.borderBlue}`, borderBottom: `1px solid ${C.borderBlue}` }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 80% at 80% 50%, rgba(79,140,255,0.08) 0%, transparent 70%)' }} />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 reveal">
            <div>
              <h2 className="font-display mb-2 shimmer-text" style={{ fontSize: 'clamp(1.6rem, 3vw, 2.4rem)' }}>
                Ready to Fix It & Grow?
              </h2>
              <p className="text-base" style={{ color: 'rgba(255,255,255,0.65)', fontFamily: "'Inter', sans-serif" }}>
                Book a free 20-minute call. No commitment, just clarity.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <button onClick={() => scrollToSection('contact')} className="btn-primary btn-primary-glow text-base">
                <Rocket size={16} /> Let's Get Started
              </button>
              <a href="mailto:techdatawebfl@gmail.com" className="btn-outline text-base">
                <Mail size={16} /> Email Directly
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="py-24" style={{ backgroundColor: C.surface }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 reveal">
            <div className="badge badge-primary mb-3 mx-auto">FAQ</div>
            <h2 className="font-display mb-3" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', color: C.text }}>
              Common Questions
            </h2>
            <div className="section-divider-center"></div>
          </div>

          <Accordion type="single" collapsible className="space-y-3 reveal">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="rounded-xl overflow-hidden glow-border"
                style={{ background: C.raised, border: `1px solid ${C.border}` }}
              >
                <AccordionTrigger className="px-5 py-4 text-left hover:no-underline" style={{ color: C.text, fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: '0.95rem' }}>
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-4" style={{ color: C.textSec, fontFamily: "'Inter', sans-serif", fontSize: '0.9rem', lineHeight: 1.7 }}>
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ===== CONTACT ===== */}
      <section id="contact" className="py-24" style={{ backgroundColor: C.base }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">

            <div className="reveal">
              <div className="badge badge-primary mb-4">Contact</div>
              <h2 className="font-display mb-4" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', color: C.text }}>
                Let's Talk About<br /><span style={{ color: C.blue }}>Your Project</span>
              </h2>
              <div className="section-divider"></div>
              <p className="text-base leading-relaxed mb-8" style={{ color: C.textSec, fontFamily: "'Inter', sans-serif" }}>
                Fill out the form and I'll get back to you within 24 hours. Or reach out directly — I'm always happy to chat.
              </p>

              <div className="space-y-4">
                {[
                  { icon: <Mail size={18} />, label: "Email", value: "techdatawebfl@gmail.com", href: "mailto:techdatawebfl@gmail.com" },
                  { icon: <Phone size={18} />, label: "Phone / WhatsApp", value: "Available on request", href: "#contact" },
                  { icon: <Target size={18} />, label: "Location", value: "Florida, USA (Remote worldwide)", href: "#" },
                ].map((item, i) => (
                  <a key={i} href={item.href} className="flex items-center gap-4 p-4 rounded-xl transition-all glow-border" style={{ background: C.surface, border: `1px solid ${C.border}`, textDecoration: 'none' }}>
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: C.blueSubtle, color: C.blue }}>
                      {item.icon}
                    </div>
                    <div>
                      <div className="text-xs mb-0.5" style={{ color: C.textMuted, fontFamily: "'Inter', sans-serif" }}>{item.label}</div>
                      <div className="text-sm font-medium" style={{ color: C.text, fontFamily: "'Inter', sans-serif" }}>{item.value}</div>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            <div className="reveal">
              <form onSubmit={handleSubmit} className="p-6 sm:p-8 rounded-2xl space-y-4 glow-border" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-xs font-medium mb-1.5" style={{ color: C.textSec, fontFamily: "'Inter', sans-serif" }}>Full Name *</label>
                    <input id="contact-name" name="name" autoComplete="name" type="text" required placeholder="Jane Smith" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} className="form-input" />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs font-medium mb-1.5" style={{ color: C.textSec, fontFamily: "'Inter', sans-serif" }}>Email *</label>
                    <input id="contact-email" name="email" autoComplete="email" type="email" required placeholder="jane@company.com" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} className="form-input" />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-organization" className="block text-xs font-medium mb-1.5" style={{ color: C.textSec, fontFamily: "'Inter', sans-serif" }}>Organization</label>
                  <input id="contact-organization" name="organization" autoComplete="organization" type="text" placeholder="Your business or organization" value={formData.org} onChange={e => setFormData(p => ({ ...p, org: e.target.value }))} className="form-input" />
                </div>

                <div>
                  <label htmlFor="contact-service" className="block text-xs font-medium mb-1.5" style={{ color: C.textSec, fontFamily: "'Inter', sans-serif" }}>Service Interested In</label>
                  <select id="contact-service" name="service" value={formData.budget} onChange={e => setFormData(p => ({ ...p, budget: e.target.value }))} className="form-input">
                    <option value="">Select a service...</option>
                    <option value="Data Management">Data Management</option>
                    <option value="Custom Dashboards">Custom Dashboards</option>
                    <option value="Website Design">Website Design</option>
                    <option value="Brand Identity">Brand Identity</option>
                    <option value="AI & Automation">AI & Automation</option>
                    <option value="Logo Design">Logo Design</option>
                    <option value="Excel Training">Excel Training</option>
                    <option value="Not sure yet">Not sure yet</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-xs font-medium mb-1.5" style={{ color: C.textSec, fontFamily: "'Inter', sans-serif" }}>Message *</label>
                  <textarea id="contact-message" name="message" required rows={4} placeholder="Tell me about your project or challenge..." value={formData.message} onChange={e => setFormData(p => ({ ...p, message: e.target.value }))} className="form-input resize-none" />
                </div>

                <div>
                  <label className="block text-xs font-medium mb-2" style={{ color: C.textSec, fontFamily: "'Inter', sans-serif" }}>Preferred Contact</label>
                  <div className="flex gap-3">
                    {['Email', 'Phone', 'Video Call'].map(opt => (
                      <button key={opt} type="button" onClick={() => setFormData(p => ({ ...p, contact: opt }))} className="flex-1 py-2 rounded-lg text-xs font-medium transition-all"
                        style={{ background: formData.contact === opt ? C.blueDark : C.raised, color: formData.contact === opt ? '#fff' : C.textSec, border: `1px solid ${formData.contact === opt ? C.blueDark : C.border}`, fontFamily: "'Inter', sans-serif" }}>
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                <button type="submit" disabled={contactMutation.isPending} className="w-full btn-primary btn-primary-glow justify-center text-base py-3" style={{ opacity: contactMutation.isPending ? 0.7 : 1 }}>
                  {contactMutation.isPending ? 'Sending...' : 'Send Message'}
                  {!contactMutation.isPending && <ArrowRight size={16} />}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer style={{ backgroundColor: C.surface, borderTop: `1px solid ${C.border}` }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <img src={LOGO_URL} alt="TDW" width={81} height={36} className="h-9 w-auto logo-glow" />
                <div>
                  <div style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: '0.9rem', color: C.text }}>TechDataWeb</div>
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.65rem', color: C.textSec }}>techdataweb.com</div>
                </div>
              </div>
              <p className="text-sm leading-relaxed max-w-xs" style={{ color: C.textSec, fontFamily: "'Inter', sans-serif" }}>
                Data management, dashboards, websites, and branding for small businesses. Based in Florida, serving clients worldwide.
              </p>
            </div>

            <div>
              <h2 className="text-sm font-semibold mb-4" style={{ color: C.text, fontFamily: "'Sora', sans-serif" }}>Services</h2>
              <ul className="space-y-2">
                {['Data Management', 'Custom Dashboards', 'Website Design', 'Brand Identity', 'AI & Automation'].map(s => (
                  <li key={s}>
                    <Link href={`/services/${s.toLowerCase().replace(/ & /g, '-').replace(/ /g, '-')}`}>
                      <span className="text-sm transition-colors cursor-pointer hover:text-blue-400" style={{ color: C.textSec, fontFamily: "'Inter', sans-serif" }}>{s}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="text-sm font-semibold mb-4" style={{ color: C.text, fontFamily: "'Sora', sans-serif" }}>Get in Touch</h2>
              <ul className="space-y-2">
                <li>
                  <a href="mailto:techdatawebfl@gmail.com" className="text-sm transition-colors hover:text-blue-400" style={{ color: C.textSec, fontFamily: "'Inter', sans-serif", textDecoration: 'none' }}>
                    techdatawebfl@gmail.com
                  </a>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')} className="text-sm transition-colors hover:text-blue-400" style={{ color: C.textSec, fontFamily: "'Inter', sans-serif" }}>
                    Book Free Consultation
                  </button>
                </li>
                <li>
                  <span className="text-sm" style={{ color: C.textSec, fontFamily: "'Inter', sans-serif" }}>Florida, USA · Remote</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="divider mb-6"></div>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
            <p className="text-xs" style={{ color: C.textSec, fontFamily: "'Inter', sans-serif" }}>
              © {new Date().getFullYear()} TechDataWeb. All rights reserved.
            </p>
            <p className="text-xs" style={{ color: C.textSec, fontFamily: "'Inter', sans-serif" }}>
              techdataweb.com · Florida, USA
            </p>
          </div>
        </div>
      </footer>

      {/* WhatsApp FAB */}
      <a
        href="https://wa.me/12392956176"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 left-6 z-50 flex items-center justify-center rounded-full shadow-xl transition-all duration-300 hover:scale-110"
        style={{ background: '#25d366', width: '52px', height: '52px' }}
        aria-label="Contact on WhatsApp"
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>

    </div>
  );
}
